import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider } from "@/context/AuthContext";
import { LoaderProvider } from "@/context/LoaderContext";
import FloatingSettings from "@/components/FloatingSettings";
import Detail from "@/pages/Detail";
import Settings from "@/pages/Settings";
import Contact from "@/pages/Contact";
import Novelties from "@/pages/Novelties";
import PatchNote from "@/pages/PatchNote";
import Tutorial from "@/pages/Tutorial";
import Questionnaire from "@/pages/Questionnaire";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import CreateAccount from "@/pages/CreateAccount";
import AdminMembres from "@/pages/AdminMembres";
import MesAccompagnes from "@/pages/MesAccompagnes";
import MonCarnet from "@/pages/MonCarnet";
import Partages from "@/pages/Partages";
import PartageLien from "@/pages/PartageLien";
import CarnetReviews from "@/pages/CarnetReviews";
import Lexique from "@/pages/Lexique";
import AdminCarnets from "@/pages/AdminCarnets";
import Hub from "@/pages/Hub";
import Landing from "@/pages/Landing";
import Ecole from "@/pages/Ecole";
import EcoleAdmin from "@/pages/EcoleAdmin";
import Formation from "@/pages/Formation";
import Bibliotheque from "@/pages/Bibliotheque";
import BiblioFiche from "@/pages/BiblioFiche";
import Profil from "@/pages/Profil";
import MembresDojo from "@/pages/Membres";
import Porte from "@/pages/Porte";
import Lecon from "@/pages/Lecon";
import Accompagnement from "@/pages/Accompagnement";
import Communaute from "@/pages/Communaute";
import BootIntro from "@/components/BootIntro";
import GuidedTour from "@/components/GuidedTour";
import ReviewAlert from "@/components/ReviewAlert";
import { getSettings, getPageConfig } from "@/lib/api";
import { INTROS } from "@/lib/intro";
import { useQueryClient } from "@tanstack/react-query";
import { applyFavicon } from "@/lib/branding";

function BrandingApplier() {
  useEffect(() => {
    const load = () => getPageConfig("branding").then((b) => { if (b?.app_icon) applyFavicon(b.app_icon); }).catch(() => {});
    load();
    window.addEventListener("branding-changed", load);
    return () => window.removeEventListener("branding-changed", load);
  }, []);
  return null;
}

function IntroGate() {
  const [show, setShow] = useState(false);
  const [config, setConfig] = useState(null);
  const qc = useQueryClient();
  const loc = useLocation();
  const triggered = useRef(false);

  useEffect(() => {
    // L'écran de démarrage cinématique se joue à l'ENTRÉE DU DŌJŌ (Hub, post-connexion),
    // jamais sur la Landing publique (page de vente).
    if (loc.pathname !== "/hub") return;
    if (triggered.current) return;
    if (sessionStorage.getItem("sx_intro_shown") === "1") return;
    if (localStorage.getItem("sx_intro_off") === "1") return;
    triggered.current = true;

    qc.fetchQuery({ queryKey: ["settings"], queryFn: getSettings })
      .then((s) => {
        let type = s.intro_type || "tengoku";
        const hasVideo = !!s.intro_has_video;
        if (type === "random") {
          const hidden = s.intro_hidden || [];
          const pool = INTROS.filter((i) => !hidden.includes(i.key)).map((i) => i.key);
          if (hasVideo) pool.push("video");
          type = pool.length ? pool[Math.floor(Math.random() * pool.length)] : "tengoku";
        }
        setConfig({ type, hasVideo, welcomeMessages: s.welcome_messages || [] });
        setShow(true);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [loc.pathname]);

  if (!show || !config) return null;
  return (
    <BootIntro
      type={config.type}
      hasVideo={config.hasVideo}
      welcomeMessages={config.welcomeMessages}
      onDone={() => { sessionStorage.setItem("sx_intro_shown", "1"); window.dispatchEvent(new Event("td-intro-done")); setShow(false); }}
    />
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppProvider>
          <BrowserRouter>
            <LoaderProvider>
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/bibliotheque" element={<Bibliotheque />} />
              <Route path="/bibliotheque/dictionnaire" element={<Lexique />} />
              <Route path="/lexique" element={<Lexique />} />
              <Route path="/bibliotheque/:id" element={<BiblioFiche />} />
              <Route path="/oeuvre/:id" element={<Detail />} />
              <Route path="/parametres" element={<Settings />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/membres" element={<MembresDojo />} />
              <Route path="/profil" element={<Profil />} />
              <Route path="/nouveautes" element={<Novelties />} />
              <Route path="/nouveautes/:id" element={<PatchNote />} />
              <Route path="/tutoriel" element={<Tutorial />} />
              <Route path="/questionnaire" element={<Questionnaire />} />
              <Route path="/connexion" element={<Auth mode="login" />} />
              <Route path="/inscription" element={<Auth mode="register" />} />
              <Route path="/creer-compte" element={<CreateAccount />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/admin/membres" element={<AdminMembres />} />
              <Route path="/carnet" element={<MonCarnet />} />
              <Route path="/carnet/relectures" element={<CarnetReviews />} />
              <Route path="/carnet/partages" element={<Partages />} />
              <Route path="/partage/:token" element={<PartageLien />} />
              <Route path="/carnet/eleves" element={<AdminCarnets />} />
              <Route path="/carnet/eleves/:userId" element={<MonCarnet />} />
              <Route path="/accompagnes" element={<MesAccompagnes />} />
              <Route path="/hub" element={<Hub />} />
              <Route path="/ecole" element={<Ecole />} />
              <Route path="/ecole/creation" element={<EcoleAdmin />} />
              <Route path="/ecole/formation/:fid" element={<Formation />} />
              <Route path="/ecole/porte/:gid" element={<Porte />} />
              <Route path="/ecole/lecon/:lid" element={<Lecon />} />
              <Route path="/accompagnement" element={<Accompagnement />} />
              <Route path="/communaute" element={<Communaute />} />
              </Routes>
              <IntroGate />
              <GuidedTour />
              <ReviewAlert />
              <BrandingApplier />
            </LoaderProvider>
          </BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
        </AppProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
